/**
 * AddCustomer — the ONE act on this area, and the only way a customer
 * organization comes into existence.
 *
 * There is no public signup. `PUBLIC_SIGNUP_ENABLED` is off and
 * `POST /api/onboarding/signup` answers 404 when it is
 * (`docs/PRODUCTION-BLOCKER-public-tenant-creation.md`). This form is the
 * operator door onto the same bootstrap primitive, with an authorization
 * decision in front of it.
 *
 * WHAT THIS FORM DOES NOT DO:
 *   - It does not derive the slug. Signup derives `<name>-<6 hex>` because
 *     there is nobody to ask; an operator IS somebody to ask, and the slug is
 *     the customer's address. A collision is a 409 and the operator chooses
 *     again — a console that silently suffixed on retry would let a retry
 *     decide what a customer is called.
 *   - It does not re-implement the server's validation. The slug pattern, the
 *     IANA timezone check and the email format all live in
 *     `createCustomerSchema`, and a second copy here would be a second opinion
 *     that can drift. The console checks only that the operator filled the
 *     field in, and renders the server's `issues[]` verbatim when it does not
 *     like something.
 */
import { useState } from 'react';
import type { ConsoleClient } from '../platform/client.js';
import type { PlatformActor } from '../platform/credentials.js';
import { createCustomer } from '../platform/tenancy.js';
import type { CreatedCustomer } from '../platform/tenancy.js';
import { Outcome } from './Outcome.js';
import type { ActResult } from './Outcome.js';

const FIELDS = [
  { id: 'name', label: 'Organization name', placeholder: 'Northside Community Services' },
  {
    id: 'slug',
    label: 'Slug (the customer’s address — used verbatim, a collision is refused)',
    placeholder: 'northside-community',
  },
  { id: 'timezone', label: 'Timezone (IANA)', placeholder: 'America/Chicago' },
  { id: 'adminName', label: 'First administrator — name', placeholder: 'Dana Okafor' },
  { id: 'adminEmail', label: 'First administrator — email', placeholder: 'dana@northside.example' },
] as const;

type FieldId = (typeof FIELDS)[number]['id'];

export function AddCustomer({
  actor,
  client,
  onCreated,
}: {
  actor: PlatformActor;
  client: ConsoleClient;
  onCreated: (created: CreatedCustomer, measuredAt: string) => void;
}) {
  const [values, setValues] = useState<Record<FieldId, string>>({
    name: '',
    slug: '',
    timezone: '',
    adminName: '',
    adminEmail: '',
  });
  const [result, setResult] = useState<ActResult | null>(null);
  const [sending, setSending] = useState(false);

  const missing = FIELDS.filter((f) => !values[f.id].trim()).map((f) => f.id);

  return (
    <section>
      <h2>Add customer</h2>
      <p className="quiet">
        Creates the organization, its first <code>org_admin</code> and that administrator’s
        invitation, in one transaction. If the second half fails the organization is suspended and
        the reply names it — nothing is left half-built and unnamed.
      </p>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (missing.length > 0 || sending) return;
          setSending(true);
          void (async () => {
            const outcome = await client<CreatedCustomer>(
              createCustomer({
                name: values.name,
                slug: values.slug,
                timezone: values.timezone,
                administrator: { name: values.adminName, email: values.adminEmail },
              }),
              actor,
            );
            setSending(false);
            setResult({ label: 'Provisioning', outcome });
            if (outcome.kind === 'ok') {
              // The measurement time is carried with the payload. Everything in
              // the reply is a reading of the tenant AT THIS INSTANT and there
              // is no read that can refresh it — see `Customers.tsx`.
              onCreated(outcome.value, new Date().toISOString());
              setValues({ name: '', slug: '', timezone: '', adminName: '', adminEmail: '' });
            }
          })();
        }}
      >
        {FIELDS.map((field) => (
          <div key={field.id}>
            <label htmlFor={`add-${field.id}`}>{field.label}</label>
            <input
              id={`add-${field.id}`}
              name={field.id}
              autoComplete="off"
              value={values[field.id]}
              placeholder={field.placeholder}
              onChange={(e) => setValues((v) => ({ ...v, [field.id]: e.target.value }))}
            />
          </div>
        ))}

        <p>
          <button type="submit" disabled={missing.length > 0 || sending}>
            {sending ? 'Provisioning…' : 'Provision customer'}
          </button>
        </p>
        {missing.length > 0 ? (
          <p className="quiet">
            Not sent yet — {missing.length} field{missing.length === 1 ? '' : 's'} still empty. The
            server, not this form, decides whether the values are valid.
          </p>
        ) : null}
      </form>

      <Outcome result={result} />
    </section>
  );
}
